import React, { createContext, useState } from 'react';

/**
 * **한 상자 안에 들어 있나** — 데스크톱에서 스탯 창과 룬 창을 한 상자로 합칠 때 true.
 * 두 창은 이걸 보고 제 바탕·테두리를 지우고, 룬 창은 별 아래에 옵션 창을 둔다(좁은 오른쪽 칸에 맞게).
 */
export const MergedPanel = createContext(false);

/**
 * **스탯 창과 룬 창을 한 자리에** —
 *
 * - 폰: [스탯 | 룬] 단추로 바꿔 본다(사용자 지시). 둘을 위아래로 쌓으면 화면이 길어지고 룬 창이 잘렸다.
 * - 데스크톱: **한 상자로 합친다**(사용자 지시 2026-10-13) — 스탯 창 오른쪽이 비어 있어서 거기에 룬 별과
 *   옵션 창을 넣는다. 늘 나란히 두고, 창이 좁으면 룬 별이 오른쪽 칸 폭에 맞춰 줄어든다(아래로 내려가면
 *   상자가 세로로 길어졌다 — 사용자 지시).
 */
export default function StatRuneSwitch({ isMobile, stat, rune, runeLabel = '룬', action, fitHeight = false }: {
    isMobile: boolean;
    stat: React.ReactNode;
    rune: React.ReactNode;
    /** 폰 전환 단추의 오른쪽 이름 (시뮬 아티팩트 칸은 '아티') */
    runeLabel?: string;
    /** 상자 안에 둘 단추(예: [룬 변경]) — 폰은 상자 머리 [스탯 | 룬] 오른쪽, 데스크톱은 상자 맨 아래 오른쪽(위에 두면 룬 별을 가린다) (사용자 2026-10-22) */
    action?: React.ReactNode;
    /**
     * 폰: 상자 높이를 **지금 보이는 창**에 맞춘다(안 보이는 창은 자리를 차지하지 않는다). 기본은 두 창 중 큰 쪽(크기 통일).
     * 전투기록 창은 스탯 창이 룬 창 높이만큼 늘어나 아래 단추가 화면 밖으로 밀렸다 (사용자 2026-10-28 "스펙창 크기를 줄여서 아래 버튼이 보이도록")
     */
    fitHeight?: boolean;
}) {
    const [tab, setTab] = useState<'stat' | 'rune'>('stat');
    if (!isMobile) {
        // 스탯 창이 없으면(몹을 아직 안 골랐다) 룬 창만 예전 모양으로
        if (!stat) return <>{rune}</>;
        return (
            <div style={{
                margin: '10px 0 6px', borderRadius: 16, padding: '14px 16px',
                background: 'linear-gradient(180deg, #2f1f16 0%, #23160f 100%)', border: '1px solid #5a3d24',
                boxShadow: '0 8px 20px rgba(0,0,0,0.35)'
            }}>
                <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 16, alignItems: 'stretch' }}>
                <MergedPanel.Provider value>
                    {/*
                      스탯 칸은 제 폭만큼, 단 460 까지 — 고치는 화면의 세트 고르기 단추 줄이 한 줄로 늘어나 오른쪽 칸을 밀어내지 않게(넘치면 줄바꿈).
                      오른쪽 칸은 최소 240 (시뮬 아티팩트 부옵 칸이 세로 한 글자 폭으로 찌그러졌다 — 2026-10-20)
                    */}
                    <div style={{ flex: '0 1 auto', minWidth: 0, maxWidth: 460 }}>{stat}</div>
                    {/* 칸 구분선 — 스탯 | 룬 */}
                    <div style={{ flex: '1 1 0', minWidth: 240, borderLeft: '1px solid #5a3d24', paddingLeft: 16 }}>{rune}</div>
                </MergedPanel.Provider>
                </div>
                {/* 상자 안 맨 아래 오른쪽 — 위에 겹쳐 띄우면 룬 별을 가린다 */}
                {action && <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10, paddingTop: 10, borderTop: '1px solid #5a3d24' }}>{action}</div>}
            </div>
        );
    }
    /**
     * 폰도 **한 상자로** (사용자 2026-10-22: 스탯·룬 탭을 아래 창과 통합) — 상자 머리에 [스탯 | 룬] 고르기와 오른쪽에 단추(룬 변경),
     * 그 아래 두 창. 두 창은 MergedPanel 로 제 바탕·테두리를 지운다(데스크톱 합친 상자와 같은 바탕).
     */
    return (
        <div style={{
            width: '100%', boxSizing: 'border-box', borderRadius: 16, padding: '8px 8px 10px',
            background: 'linear-gradient(180deg, #2f1f16 0%, #23160f 100%)', border: '1px solid #5a3d24', boxShadow: '0 8px 20px rgba(0,0,0,0.35)'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ display: 'inline-flex', borderRadius: 9, overflow: 'hidden', border: '1px solid #7a4a22' }}>
                    {(['stat', 'rune'] as const).map((t) => (
                        <button
                            key={t}
                            type="button"
                            onClick={() => setTab(t)}
                            style={{
                                padding: '5px 18px', border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 800,
                                background: tab === t ? '#7a4a22' : '#1a0f08',
                                color: tab === t ? '#ffe2b0' : '#b8a58c'
                            }}
                        >{t === 'stat' ? '스탯' : runeLabel}</button>
                    ))}
                </div>
                {action && <div style={{ marginLeft: 'auto' }}>{action}</div>}
            </div>
            {/*
              두 창을 **같은 칸에 겹쳐 둔다** — 칸 높이가 둘 중 큰 쪽이 되어 두 창 크기가 같다(사용자 지시: 크기 통일).
              안 보이는 쪽은 자리만 차지한다. 안쪽도 격자라 창이 칸 높이까지 늘어난다.
            */}
            <MergedPanel.Provider value>
                <div style={{ display: 'grid' }}>
                    <div style={{ gridArea: '1 / 1', display: fitHeight && tab !== 'stat' ? 'none' : 'grid', visibility: tab === 'stat' ? 'visible' : 'hidden' }}>{stat}</div>
                    <div style={{ gridArea: '1 / 1', display: fitHeight && tab !== 'rune' ? 'none' : 'grid', visibility: tab === 'rune' ? 'visible' : 'hidden' }}>{rune}</div>
                </div>
            </MergedPanel.Provider>
        </div>
    );
}
